import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import supabase from '../../supabaseClient';
import { setUser, setSession, setAuthLoading, setCredits, setIsAdmin } from '../../store/authSlice';
import { fetchCredits } from '../../services/documentApi';
import { acceptTerms } from '../../services/termsApi';
import AuthModal from './AuthModal';

const LOADING_SCREEN = (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#0A0F1E',
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#6B7FA8',
    fontSize: '13px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  }}>
    Loading…
  </div>
);

/**
 * AuthProvider — initialises the Supabase session into Redux on mount and
 * keeps it in sync via onAuthStateChange. Also fetches credits whenever the
 * session is established so RequireCredits has a loaded value before routing.
 */
export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  // Dedupes across getSession() + repeated onAuthStateChange fires (e.g.
  // TOKEN_REFRESHED every ~hour) for the same user, so ensureTermsAccepted
  // below doesn't re-check/re-POST on every one of those. Keyed by user id,
  // not a single boolean, so switching accounts in one tab still checks the
  // new user. Lives for the component's lifetime, which is the app's — this
  // provider wraps <Routes> once in App.js and never remounts on navigation.
  const termsCheckedUserIdsRef = useRef(new Set());

  useEffect(() => {
    function ensureTermsAccepted(session) {
      const userId = session?.user?.id;
      if (!userId || termsCheckedUserIdsRef.current.has(userId)) return;
      termsCheckedUserIdsRef.current.add(userId);

      supabase
        .from('terms_acceptances')
        .select('id')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle()
        .then(({ data, error }) => {
          if (error) {
            console.error('[terms] acceptance check failed:', error.message);
            return;
          }
          if (!data) {
            acceptTerms(session.access_token);
          }
        });
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      dispatch(setSession(session));
      dispatch(setUser(session?.user ?? null));
      dispatch(setAuthLoading(false));
      if (session?.access_token) {
        fetchCredits(session.access_token).then(data => {
          if (data !== null) {
            dispatch(setCredits(data.credits));
            dispatch(setIsAdmin(data.isAdmin));
          }
        });
        ensureTermsAccepted(session);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      dispatch(setSession(session));
      dispatch(setUser(session?.user ?? null));
      dispatch(setAuthLoading(false));
      if (session?.access_token) {
        fetchCredits(session.access_token).then(data => {
          if (data !== null) {
            dispatch(setCredits(data.credits));
            dispatch(setIsAdmin(data.isAdmin));
          }
        });
        ensureTermsAccepted(session);
      } else {
        dispatch(setCredits(null));
        dispatch(setIsAdmin(false));
      }
    });

    return () => subscription.unsubscribe();
  }, [dispatch]);

  return children;
}

/**
 * RequireAuth — route-level guard. Shows the loading screen while the session
 * bootstraps, then shows the non-dismissable AuthModal if there is no session,
 * or renders children when authenticated.
 */
export function RequireAuth({ children }) {
  const { session, loading } = useSelector((state) => state.auth);

  if (loading) return LOADING_SCREEN;
  if (!session) return <AuthModal />;
  return children;
}

/**
 * RequireCredits — route-level guard. Must be nested inside RequireAuth.
 * Waits for credits to load, then redirects to /get-credits when credits
 * are 0 or null (unknown), or renders children when credits > 0.
 */
export function RequireCredits({ children }) {
  const { credits, loading } = useSelector((state) => state.auth);

  if (loading) return null;

  if (credits === null) return LOADING_SCREEN;

  if (credits === 0) return <Navigate to="/get-credits" replace />;

  return children;
}
